import type { EvaluationContext, UserContext } from "./types";

/**
 * フラグ評価用のコンテキストを構築する Builder クラス
 *
 * Method chain でコンテキストを段階的に構築できる
 */
class FlagContextBuilder {
  private context: EvaluationContext = {};

  /**
   * ユーザー情報を追加
   *
   * @param user - ユーザーオブジェクト
   * @returns this (method chain 用)
   *
   * @example
   * ```typescript
   * builder.withUser(user)
   * ```
   */
  withUser(
    user:
      | {
          userId?: string | null;
        }
      | null
      | undefined
  ): this {
    if (!user) return this;

    const userContext: UserContext = {};
    if (user.userId) userContext.userId = user.userId;

    // ユーザーコンテキストが空でない場合のみ追加
    if (Object.keys(userContext).length > 0) {
      this.context = { ...this.context, user: userContext };
    }

    return this;
  }

  /**
   * カスタムコンテキストを追加（既存とマージ）
   *
   * @param customContext - 追加するカスタムコンテキスト
   * @returns this (method chain 用)
   *
   * @example
   * ```typescript
   * builder.with({ source: 'webapp', version: '1.0' })
   * ```
   */
  with(customContext: Record<string, string | number | boolean>): this {
    this.context = { ...this.context, ...customContext };
    return this;
  }

  /**
   * 最終的な EvaluationContext を返す
   *
   * @returns 構築された EvaluationContext
   */
  build(): EvaluationContext {
    return this.context;
  }
}

/**
 * フラグ評価用のコンテキストを構築
 *
 * Method chain で段階的にコンテキストを構築できる Builder を返す
 *
 * @example
 * ```typescript
 * const context = buildFlagContext()
 *   .withUser(user)
 *   .with({ source: 'webapp', version: '1.0' })
 *   .build();
 *
 * OpenFeature.setContext(context);
 * ```
 */
export function buildFlagContext(): FlagContextBuilder {
  return new FlagContextBuilder();
}
